import type { Rng } from '../../core/rng';
import type { Vec3 } from '../../geo/types';
import type { GunSmokeEnd } from './harbourZones';
import {
  surfaceNet,
  SurfaceLeavesBox,
  type Box,
  type Face,
  type ImplicitSurface,
  type SurfaceMesh,
} from './surfaceNet';

/**
 * A painted puff (docs/reference/ship-measure/harbour-count.json): how far it reaches out from the
 * hull end that fires it and how high the painter draws it, both as shares of its ship's hull
 * length, and how many round crowns it rolls out in. The cloud rides in the ship's frame, so the
 * ship's painter scale widens cloud and hull alike and these shares of the true hull length are
 * the painted ones.
 */
export interface PuffSize {
  readonly length: number;
  readonly height: number;
  readonly billows: number;
}

/* Most painted puffs swell away from the muzzle: the billows grow from 0.8 to 1.15 of the mean
   radius along the cloud, each drawn up to 10% either way, and stand a little above or below and
   beside the ship's axis, so the cloud rolls rather than lies in a straight row. */
const BILLOW_SWELL = [0.8, 1.15] as const;
const BILLOW_JITTER = 0.1;
const BILLOW_WANDER = 0.2;

/* Seen from 43-48 degrees up (summary.json) the painter camera draws a cloud abeam from the foot
   of its billows to the tufts on its highest crown, 2.4 of their mean radius with the swell and the
   wander: a billow's mean radius is 0.41 of the painted height. Neighbouring billows stand 1.7 of
   their mean radius apart, so each keeps its own round crown and the cloud dips between them, as
   the painted billows do. */
const BILLOW_RADIUS_PER_HEIGHT = 0.41;
const BILLOW_SPACING = 1.7;

/* A main billow's centre stands 0.65 of its radius above the water, which hides the rest: the
   cloud sits on the IJ with a flat foot. */
const CENTRE_ABOVE_WATER = 0.65;

/* The painted puffs roll over on their upper rims: a row of four tufts swells out along each
   billow's crest, in the upright plane through its centre along the cloud, from 40 degrees above
   the axis on the side away from the ship to 140 on the side towards it, each drawn up to 8
   degrees either way. Each is a third to a half of the billow across and stands out of it by a
   quarter of its radius. In one row no tuft stands before another where the cloud is seen abeam,
   and the outline would ink the rim of one standing before another. */
export const GUN_SMOKE_TUFTS_PER_BILLOW = 4;
const TUFT_RADIUS = [0.3, 0.45] as const;
const TUFT_STANDS_OUT = 0.25;
const TUFT_CREST_DEG = [40, 140] as const;
const TUFT_JITTER_DEG = 8;

/* Between two billows a smaller one lies centred on the water, 0.55 of their mean radius, so the
   cloud rests on the IJ along its whole length and dips only between the crowns. */
const FOOT_RADIUS = 0.55;

/* Width of the soft union of the billows, as a share of the mean radius: enough to round every
   neck into one smooth surface, little enough that each billow keeps its own round outline. */
const BLEND = 0.2;

/* Each billow is lit as a ball of its own, as the painter lit every puff. The shading normals,
   the soft union's gradient at each vertex, are averaged with their neighbours' across the faces
   twice over, so round a neck or a tuft the light turns over a few cells rather than at one edge,
   and neighbouring pixels never part by the outline's crease angle. */
const NORMAL_SMOOTHING = 2;
const DEG = Math.PI / 180;

/* The surface is meshed on a grid of a quarter of the mean radius, 1.05 m for a kraeck: seen from
   30 m a billow's outline breaks into chords of about 30 px that stray from its circle by about a
   pixel, and a tuft spans two to four cells. A finer grid costs the ships stage its budget. */
const CELL_PER_RADIUS = 1 / 4;

/** One ball of smoke in a cloud's own frame: x out from the firing end, y up from the water. */
export interface Billow {
  readonly centre: Vec3;
  readonly radius: number;
}

/** The billows of one cloud: the main ones along the ship axis, their tufts, and their feet, and
    how far the cloud is drawn out along the axis to its puff's length. */
export interface CloudBillows {
  readonly main: readonly Billow[];
  readonly tufts: readonly (readonly Billow[])[];
  readonly feet: readonly Billow[];
  readonly stretch: number;
}

function mainBillows(shipLength: number, size: PuffSize, rng: Rng): Billow[] {
  const mean = BILLOW_RADIUS_PER_HEIGHT * size.height * shipLength;
  const last = Math.max(1, size.billows - 1);
  const radii = Array.from({ length: size.billows }, (_, i) => {
    const swell = BILLOW_SWELL[0] + ((BILLOW_SWELL[1] - BILLOW_SWELL[0]) * i) / last;
    return mean * swell * rng.range(1 - BILLOW_JITTER, 1 + BILLOW_JITTER);
  });
  const along = radii.reduce<number[]>(
    (xs, radius, i) => [
      ...xs,
      i === 0 ? radius : xs[i - 1]! + (BILLOW_SPACING * (radii[i - 1]! + radius)) / 2,
    ],
    []
  );
  return radii.map((radius, i): Billow => {
    const rise = CENTRE_ABOVE_WATER + rng.range(-BILLOW_WANDER, BILLOW_WANDER);
    const aside = rng.range(-BILLOW_WANDER, BILLOW_WANDER);
    return { centre: [along[i]!, rise * radius, aside * radius], radius };
  });
}

function tuftsOf(billow: Billow, rng: Rng): Billow[] {
  const [x, y, z] = billow.centre;
  const [from, to] = TUFT_CREST_DEG;
  const step = (to - from) / (GUN_SMOKE_TUFTS_PER_BILLOW - 1);
  return Array.from({ length: GUN_SMOKE_TUFTS_PER_BILLOW }, (_, k): Billow => {
    const up = (from + step * k + rng.range(-TUFT_JITTER_DEG, TUFT_JITTER_DEG)) * DEG;
    const radius = rng.range(...TUFT_RADIUS) * billow.radius;
    const reach = billow.radius * (1 + TUFT_STANDS_OUT) - radius;
    return { centre: [x + reach * Math.cos(up), y + reach * Math.sin(up), z], radius };
  });
}

function footBetween(a: Billow, b: Billow): Billow {
  return {
    centre: [(a.centre[0] + b.centre[0]) / 2, 0, (a.centre[2] + b.centre[2]) / 2],
    radius: (FOOT_RADIUS * (a.radius + b.radius)) / 2,
  };
}

/* The painter draws his crowns broader than they stand high (harbour-count.json: a puff 1.6-2.6
   times as long as high in two or three crowns); the cloud of round billows is drawn out along its
   axis until it reaches its puff's length. */
function stretchOf(main: readonly Billow[], shipLength: number, size: PuffSize): number {
  const last = main[main.length - 1]!;
  return (size.length * shipLength) / (last.centre[0] + last.radius);
}

/** The billows of one cloud for a ship of this length and a puff of this size. */
export function cloudBillows(shipLength: number, size: PuffSize, rng: Rng): CloudBillows {
  const main = mainBillows(shipLength, size, rng);
  return {
    main,
    tufts: main.map((billow) => tuftsOf(billow, rng)),
    feet: main.slice(1).map((billow, i) => footBetween(main[i]!, billow)),
    stretch: stretchOf(main, shipLength, size),
  };
}

/** Every billow of a cloud, for its field. */
export function billowsOf(cloud: CloudBillows): readonly Billow[] {
  return [...cloud.main, ...cloud.tufts.flat(), ...cloud.feet];
}

/* Balls farther than this many blends past the nearest weigh less than e^-12 in the soft union. */
const NEGLIGIBLE_BLENDS = 12;

/**
 * Signed distance-like field of the billows' soft union, negative inside: an exponential smooth
 * minimum of the distances to the balls, whose gradient, the weighted mean of the balls' outward
 * directions, is never longer than one.
 */
export function cloudField(billows: readonly Billow[], blend: number): ImplicitSurface {
  const count = billows.length;
  const centres = Float64Array.from(billows.flatMap((b) => [...b.centre]));
  const radii = Float64Array.from(billows.map((b) => b.radius));
  const distances = new Float64Array(count);
  const weights = new Float64Array(count);
  const swell = blend * Math.log(count);
  const nearest = (p: Vec3): number => {
    let near = Infinity;
    for (let i = 0; i < count; i++) {
      const dx = p[0] - centres[3 * i]!;
      const dy = p[1] - centres[3 * i + 1]!;
      const dz = p[2] - centres[3 * i + 2]!;
      distances[i] = Math.sqrt(dx * dx + dy * dy + dz * dz) - radii[i]!;
      if (distances[i]! < near) near = distances[i]!;
    }
    return near;
  };
  const softened = (near: number): number => {
    let sum = 0;
    for (let i = 0; i < count; i++) {
      const excess = (distances[i]! - near) / blend;
      weights[i] = excess > NEGLIGIBLE_BLENDS ? 0 : Math.exp(-excess);
      sum += weights[i]!;
    }
    return near - blend * Math.log(sum);
  };
  const value = (p: Vec3): number => softened(nearest(p));
  /* The union lies within `swell` outside the balls, so inside a ball, or beyond the swell, the
     nearest ball gives the field's sign. */
  const sample = (p: Vec3): number => {
    const near = nearest(p);
    return near < 0 || near > swell ? near : softened(near);
  };
  const withGradient = (p: Vec3): readonly [number, Vec3] => {
    const field = value(p);
    let [sum, gx, gy, gz] = [0, 0, 0, 0];
    for (let i = 0; i < count; i++) {
      const weight = weights[i]!;
      if (weight === 0) continue;
      const length = distances[i]! + radii[i]!;
      sum += weight;
      gx += (weight * (p[0] - centres[3 * i]!)) / length;
      gy += (weight * (p[1] - centres[3 * i + 1]!)) / length;
      gz += (weight * (p[2] - centres[3 * i + 2]!)) / length;
    }
    return [field, [gx / sum, gy / sum, gz / sum]];
  };
  return { value, sample, withGradient };
}

/** Width of the soft union for a cloud of these billows. */
export function blendOf(cloud: CloudBillows): number {
  return (BLEND * cloud.main.reduce((sum, b) => sum + b.radius, 0)) / cloud.main.length;
}

/* The box around the balls, with this margin on every side. */
function boxAround(billows: readonly Billow[], margin: number): Box {
  const bound = (axis: number, sign: 1 | -1): number =>
    sign * Math.max(...billows.map((b) => sign * b.centre[axis]! + b.radius)) + sign * margin;
  return {
    min: [bound(0, -1), bound(1, -1), bound(2, -1)],
    max: [bound(0, 1), bound(1, 1), bound(2, 1)],
  };
}

/* Where some balls meet, the soft union swells past them by up to the blend times the log of their
   number, which the cloud's ends and flanks, near a few balls each, never reach: it is meshed in a
   box with room for four balls' swell, and in one with room for all of them only if its surface
   runs out of the first. The net finds the surface going up from the centre of every ball. */
function meshOf(
  field: ImplicitSurface,
  billows: readonly Billow[],
  blend: number,
  cell: number
): SurfaceMesh {
  const centres = billows.map((b) => b.centre);
  try {
    return surfaceNet(field, boxAround(billows, blend * Math.log(4) + 2 * cell), cell, centres);
  } catch (error) {
    if (!(error instanceof SurfaceLeavesBox)) throw error;
    const room = blend * Math.log(billows.length) + 2 * cell;
    return surfaceNet(field, boxAround(billows, room), cell, centres);
  }
}

function unit(v: Vec3): Vec3 {
  const length = Math.hypot(v[0], v[1], v[2]);
  return [v[0] / length, v[1] / length, v[2] / length];
}

/* Each normal averaged with its neighbours' across the faces, `times` over. */
function smoothedNormals(
  normals: readonly Vec3[],
  faces: readonly Face[],
  times: number
): readonly Vec3[] {
  const flat = new Float64Array(3 * normals.length);
  normals.forEach((normal, i) => {
    flat.set(normal, 3 * i);
  });
  const passes = Array.from({ length: times }).reduce<Float64Array>((current) => {
    const next = current.slice();
    for (const face of faces) {
      for (let u = 0; u < 3; u++) {
        const a = 3 * face[u]!;
        for (let w = 0; w < 3; w++) {
          if (w === u) continue;
          const b = 3 * face[w]!;
          next[a] = next[a]! + current[b]!;
          next[a + 1] = next[a + 1]! + current[b + 1]!;
          next[a + 2] = next[a + 2]! + current[b + 2]!;
        }
      }
    }
    return next;
  }, flat);
  return normals.map((_, i) => unit([passes[3 * i]!, passes[3 * i + 1]!, passes[3 * i + 2]!]));
}

/* The mesh drawn out `stretch` times along the cloud's axis, each normal turned as the stretch
   turns the surface. */
function drawnOut(mesh: SurfaceMesh, stretch: number): SurfaceMesh {
  return {
    points: mesh.points.map((p): Vec3 => [p[0] * stretch, p[1], p[2]]),
    normals: mesh.normals.map((n) => unit([n[0] / stretch, n[1], n[2]])),
    faces: mesh.faces,
  };
}

/**
 * A static cloud of powder smoke for a ship of this length and a puff of this size, in the
 * cloud's own frame, x out from the end that fires it: the soft union of billows of several sizes
 * on the water, drawn out to the puff's length, as one closed surface wound outwards, with the
 * shading normal of every point.
 */
export function cloudShape(shipLength: number, size: PuffSize, rng: Rng): SurfaceMesh {
  const cloud = cloudBillows(shipLength, size, rng);
  const blend = blendOf(cloud);
  const mean = blend / BLEND;
  const field = cloudField(billowsOf(cloud), blend);
  const mesh = meshOf(field, billowsOf(cloud), blend, CELL_PER_RADIUS * mean);
  const smooth = { ...mesh, normals: smoothedNormals(mesh.normals, mesh.faces, NORMAL_SMOOTHING) };
  return drawnOut(smooth, cloud.stretch);
}

/**
 * A cloud shape grown by `scale` and laid in the ship's frame (bow at -X), from the stem or the
 * sternpost out along the ship's axis.
 */
export function atFiringEnd(
  shape: SurfaceMesh,
  scale: number,
  shipLength: number,
  end: GunSmokeEnd
): SurfaceMesh {
  const outward = end === 'bow' ? -1 : 1;
  const half = shipLength / 2;
  return {
    points: shape.points.map((p): Vec3 => [
      outward * (half + scale * p[0]),
      scale * p[1],
      scale * p[2],
    ]),
    normals: shape.normals.map((n): Vec3 => [outward * n[0], n[1], n[2]]),
    faces: outward > 0 ? shape.faces : shape.faces.map((f): Face => [f[0], f[2], f[1]]),
  };
}

/** A cloud of its own for a ship of this length and a puff of this size, at the end that fires
    it, in the ship's frame. */
export function gunSmokeCloud(
  shipLength: number,
  end: GunSmokeEnd,
  rng: Rng,
  size: PuffSize
): SurfaceMesh {
  return atFiringEnd(cloudShape(shipLength, size, rng), 1, shipLength, end);
}
